import {useState, useEffect} from "react"
import {
    Box, Typography, Paper, Grid, TextField, Button, Switch, FormControlLabel,
    Divider, List, ListItem, ListItemText, ListItemSecondaryAction, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions, Chip, Snackbar, Alert,
    Card, CardContent, CardActions,
} from "@mui/material"
import {
    Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
    Save as SaveIcon, Category as CategoryIcon,
    Notifications as NotificationsIcon, Security as SecurityIcon,
    Tune as TuneIcon,
} from "@mui/icons-material"
import {useTranslation} from "react-i18next"
import axiosInstance from "../../config/axios-config"

export default function TabAdminSettings({tabValue}) {
    const {t} = useTranslation()
    const [categories, setCategories] = useState([])
    const [settings, setSettings] = useState({
        maintenanceMode: false,
        registrationEnabled: true,
        emailNotifications: true,
        orderAutoConfirm: false,
        maxProductsPerPage: 20,
        sessionTimeout: 30,
        minPasswordLength: 12,
    })
    const [openCategoryDialog, setOpenCategoryDialog] = useState(false)
    const [categoryName, setCategoryName] = useState("")
    const [editingCategory, setEditingCategory] = useState(null)
    const [snackbar, setSnackbar] = useState({open: false, message: "", severity: "success"})

    const fetchSettings = async () => {
        try {
            const response = await axiosInstance.get("/api/v1/admin/settings")
            setSettings(response.data)
        } catch (error) {
            console.error("Error fetching settings:", error)
            setSnackbar({open: true, message: error.response?.data?.message || t("AdminRequestFailed"), severity: "error"})
        }
    }

    const fetchCategories = async () => {
        try {
            const response = await axiosInstance.get("/api/v1/admin/categories")
            setCategories(response.data || [])
        } catch (error) {
            console.error("Error fetching categories:", error)
            setSnackbar({open: true, message: error.response?.data?.message || t("AdminRequestFailed"), severity: "error"})
        }
    }

    useEffect(() => {
        if (tabValue === 4) {
            fetchSettings()
            fetchCategories()
        }
    }, [tabValue])

    const handleSaveSettings = async () => {
        try {
            await axiosInstance.put("/api/v1/admin/settings", settings)
            setSnackbar({open: true, message: t("SettingsSaved") || "Settings saved successfully", severity: "success"})
        } catch (error) {
            console.error("Error saving settings:", error)
            setSnackbar({open: true, message: t("ErrorSavingSettings") || "Error saving settings", severity: "error"})
        }
    }

    const handleSaveCategory = async () => {
        try {
            const url = editingCategory
                ? `/api/v1/admin/categories/${editingCategory.id}`
                : "/api/v1/admin/categories"
            await axiosInstance.request({url, method: editingCategory ? "PUT" : "POST", data: {name: categoryName}})
            setOpenCategoryDialog(false)
            setCategoryName("")
            setEditingCategory(null)
            fetchCategories()
        } catch (error) {
            console.error("Error saving category:", error)
            setSnackbar({open: true, message: error.response?.data?.message || t("AdminRequestFailed"), severity: "error"})
        }
    }

    const handleDeleteCategory = async (categoryId) => {
        if (!window.confirm(t("ConfirmDeleteCategory") || "Are you sure you want to delete this category?")) return
        try {
            await axiosInstance.delete(`/api/v1/admin/categories/${categoryId}`)
            fetchCategories()
        } catch (error) {
            console.error("Error deleting category:", error)
            setSnackbar({open: true, message: error.response?.data?.message || t("AdminRequestFailed"), severity: "error"})
        }
    }

    if (tabValue !== 4) return null

    return (
        <Box>
            <Grid container spacing={3}>
                {/* General Settings */}
                <Grid item xs={12} md={6}>
                    <Card elevation={1}>
                        <CardContent>
                            <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 2}}>
                                <TuneIcon color="primary" />
                                <Typography variant="h6">{t("GeneralSettings") || "General Settings"}</Typography>
                            </Box>
                            <Divider sx={{mb: 2}} />
                            <Box sx={{display: "flex", flexDirection: "column", gap: 2}}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={settings.maintenanceMode}
                                            onChange={(e) => setSettings({...settings, maintenanceMode: e.target.checked})}
                                            color="warning"
                                        />
                                    }
                                    label={t("MaintenanceMode") || "Maintenance Mode"}
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={settings.registrationEnabled}
                                            onChange={(e) => setSettings({...settings, registrationEnabled: e.target.checked})}
                                        />
                                    }
                                    label={t("AllowRegistration") || "Allow New Registrations"}
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={settings.orderAutoConfirm}
                                            onChange={(e) => setSettings({...settings, orderAutoConfirm: e.target.checked})}
                                        />
                                    }
                                    label={t("AutoConfirmOrders") || "Auto-Confirm Orders"}
                                />
                                <TextField
                                    label={t("MaxProductsPerPage") || "Max Products Per Page"}
                                    type="number"
                                    size="small"
                                    value={settings.maxProductsPerPage}
                                    onChange={(e) => setSettings({...settings, maxProductsPerPage: parseInt(e.target.value, 10)})}
                                />
                            </Box>
                        </CardContent>
                        <CardActions sx={{justifyContent: "flex-end", px: 2, pb: 2}}>
                            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveSettings}>
                                {t("SaveSettings") || "Save Settings"}
                            </Button>
                        </CardActions>
                    </Card>
                </Grid>

                {/* Security Settings */}
                <Grid item xs={12} md={6}>
                    <Card elevation={1}>
                        <CardContent>
                            <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 2}}>
                                <SecurityIcon color="error" />
                                <Typography variant="h6">{t("SecuritySettings") || "Security Settings"}</Typography>
                            </Box>
                            <Divider sx={{mb: 2}} />
                            <Box sx={{display: "flex", flexDirection: "column", gap: 2}}>
                                <TextField
                                    label={t("SessionTimeout") || "Session Timeout (minutes)"}
                                    type="number"
                                    size="small"
                                    value={settings.sessionTimeout}
                                    onChange={(e) => setSettings({...settings, sessionTimeout: parseInt(e.target.value, 10)})}
                                />
                                <TextField
                                    label={t("MinPasswordLength") || "Minimum Password Length"}
                                    type="number"
                                    size="small"
                                    value={settings.minPasswordLength}
                                    onChange={(e) => setSettings({...settings, minPasswordLength: parseInt(e.target.value, 10)})}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Notification Settings */}
                <Grid item xs={12} md={6}>
                    <Card elevation={1}>
                        <CardContent>
                            <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 2}}>
                                <NotificationsIcon color="info" />
                                <Typography variant="h6">{t("NotificationSettings") || "Notification Settings"}</Typography>
                            </Box>
                            <Divider sx={{mb: 2}} />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={settings.emailNotifications}
                                        onChange={(e) => setSettings({...settings, emailNotifications: e.target.checked})}
                                    />
                                }
                                label={t("EmailNotifications") || "Email Notifications"}
                            />
                        </CardContent>
                    </Card>
                </Grid>

                {/* Product Categories */}
                <Grid item xs={12} md={6}>
                    <Card elevation={1}>
                        <CardContent>
                            <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2}}>
                                <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                                    <CategoryIcon color="success" />
                                    <Typography variant="h6">{t("ProductCategories") || "Product Categories"}</Typography>
                                </Box>
                                <Button
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={() => { setCategoryName(""); setEditingCategory(null); setOpenCategoryDialog(true) }}
                                >
                                    {t("Add") || "Add"}
                                </Button>
                            </Box>
                            <Divider sx={{mb: 1}} />
                            <List dense>
                                {categories.length === 0 ? (
                                    <ListItem>
                                        <ListItemText primary={t("NoCategoriesFound") || "No categories found"} sx={{color: "text.secondary"}} />
                                    </ListItem>
                                ) : (
                                    categories.map((category) => (
                                        <ListItem key={category.id} divider>
                                            <ListItemText
                                                primary={
                                                    <Chip label={category.name} size="small" variant="outlined" color="success" />
                                                }
                                            />
                                            <ListItemSecondaryAction>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => { setCategoryName(category.name); setEditingCategory(category); setOpenCategoryDialog(true) }}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton size="small" color="error" onClick={() => handleDeleteCategory(category.id)}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                    ))
                                )}
                            </List>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Category Dialog */}
            <Dialog open={openCategoryDialog} onClose={() => setOpenCategoryDialog(false)} maxWidth="xs" fullWidth>
                <DialogTitle>
                    {editingCategory ? t("EditCategory") || "Edit Category" : t("AddCategory") || "Add Category"}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        label={t("CategoryName") || "Category Name"}
                        value={categoryName}
                        onChange={(e) => setCategoryName(e.target.value)}
                        sx={{mt: 1}}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenCategoryDialog(false)}>{t("Cancel") || "Cancel"}</Button>
                    <Button variant="contained" onClick={handleSaveCategory} disabled={!categoryName.trim()}>
                        {t("Save") || "Save"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({...snackbar, open: false})}
                anchorOrigin={{vertical: "bottom", horizontal: "right"}}
            >
                <Alert onClose={() => setSnackbar({...snackbar, open: false})} severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    )
}
